import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, CheckCircle, ChevronDown } from 'lucide-react';
import FadeIn from './FadeIn';
import { toast } from 'sonner';

const WebinarRegistration = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    webinar_id: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWebinars();
  }, []);

  const fetchWebinars = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/webinars/upcoming`);
      const data = await response.json();
      setWebinars(data.webinars || []);
    } catch (error) {
      console.error('Fetch webinars error:', error);
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    const formatted = date.toLocaleDateString('cs-CZ', options);
    const time = date.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    return `${formatted} v ${time}`;
  };

  const formatDateShort = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
    const time = date.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    return `${day} ${time}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.webinar_id) {
      toast.error('Vyberte prosím termín webináře');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email,
          name: formData.name,
          phone: formData.phone,
          school: formData.school,
          source: 'webinar',
          webinar_id: formData.webinar_id
        }),
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Registrace úspěšná! Potvrzení s pozvánkou do kalendáře jsme odeslali na váš email.');
        setTimeout(() => {
          setSubmitted(false);
          setFormData({ name: '', email: '', phone: '', school: '', webinar_id: '' });
        }, 8000);
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Něco se pokazilo. Zkuste to prosím znovu.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const selectedWebinar = webinars.find(w => w.id === formData.webinar_id);

  return (
    <section id="webinar" data-testid="webinar-section" className="py-20 bg-gradient-to-b from-white to-[color:var(--bg)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left - Info */}
          <FadeIn>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[color:var(--text-primary)] mb-6">
                Bezplatný webinář
              </h2>
              <p className="text-lg text-[color:var(--text-secondary)] mb-8">
                Praktická ukázka Školobota v akci. Ukážeme vám, co všechno Školobot umí a jak ho využijete ve vaší škole.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[color:var(--primary-100)] rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-[color:var(--primary-600)]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[color:var(--text-primary)]">Kdy</div>
                    <div className="text-sm text-[color:var(--text-secondary)]">
                      {selectedWebinar ? formatDate(selectedWebinar.date) : 'Vyberte termín ve formuláři'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[color:var(--primary-100)] rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[color:var(--primary-600)]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[color:var(--text-primary)]">Délka</div>
                    <div className="text-sm text-[color:var(--text-secondary)]">
                      {selectedWebinar ? `${selectedWebinar.duration_minutes} minut včetně Q&A` : '30 minut včetně Q&A'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[color:var(--primary-100)] rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-[color:var(--primary-600)]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[color:var(--text-primary)]">Pro koho</div>
                    <div className="text-sm text-[color:var(--text-secondary)]">Pedagogy a vedení škol všech stupňů</div>
                  </div>
                </div>
              </div>

              <div className="bg-[color:var(--primary-50)] border-2 border-[color:var(--primary-200)] rounded-2xl p-6">
                <h4 className="font-bold text-[color:var(--text-primary)] mb-3">Co se na webináři dozvíte:</h4>
                <ul className="space-y-2 text-sm text-[color:var(--text-secondary)]">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[color:var(--primary-600)] flex-shrink-0 mt-0.5" />
                    <span>Jak Školobot funguje a co všechno umí</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[color:var(--primary-600)] flex-shrink-0 mt-0.5" />
                    <span>Čím se liší od běžných AI nástrojů</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[color:var(--primary-600)] flex-shrink-0 mt-0.5" />
                    <span>Jak ho využijí pedagogové i vedení školy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[color:var(--primary-600)] flex-shrink-0 mt-0.5" />
                    <span>Jak si Školobot můžete vyzkoušet zdarma</span>
                  </li>
                </ul>
              </div>
            </div>
          </FadeIn>

          {/* Right - Form */}
          <FadeIn delay={0.2}>
            <div className="bg-white border-2 border-[color:var(--primary-200)] rounded-3xl p-8 md:p-10 shadow-xl">
              <h3 className="text-2xl font-bold text-[color:var(--text-primary)] mb-2">
                Registrace na webinář
              </h3>
              <p className="text-sm text-[color:var(--text-secondary)] mb-6">
                Vyberte termín a vyplňte formulář. Potvrzení s pozvánkou do kalendáře vám přijde emailem.
              </p>

              {submitted ? (
                <div className="bg-[color:var(--primary-50)] border-2 border-[color:var(--primary-200)] rounded-xl p-6 text-center">
                  <CheckCircle className="w-12 h-12 text-[color:var(--primary-600)] mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-[color:var(--text-primary)] mb-2">Děkujeme za registraci!</h4>
                  <p className="text-sm text-[color:var(--text-secondary)]">
                    Potvrzení a pozvánku do kalendáře jsme poslali na váš email.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="webinar-form">
                  {/* Webinar date selection */}
                  <div>
                    <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                      Termín webináře *
                    </label>
                    {loading ? (
                      <div className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl bg-gray-50 text-gray-400">
                        Načítám termíny...
                      </div>
                    ) : webinars.length === 0 ? (
                      <div className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl bg-orange-50 text-orange-700 text-sm">
                        Aktuálně nejsou vypsány žádné termíny. Kontaktujte nás na info@skolobot.cz pro domluvení individuální ukázky.
                      </div>
                    ) : (
                      <div className="relative">
                        <select
                          name="webinar_id"
                          value={formData.webinar_id}
                          onChange={handleChange}
                          required
                          data-testid="webinar-date-select"
                          className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors appearance-none bg-white cursor-pointer"
                        >
                          <option value="">-- Vyberte termín --</option>
                          {webinars.map(w => (
                            <option key={w.id} value={w.id} disabled={w.is_full}>
                              {formatDateShort(w.date)} — {w.title}{w.is_full ? ' (obsazeno)' : w.spots_left ? ` (zbývá ${w.spots_left} míst)` : ''}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                      Jméno a příjmení *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="webinar-name-input"
                      className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors"
                      placeholder="Jana Nováková"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="webinar-email-input"
                      className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors"
                      placeholder="jana.novakova@skola.cz"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="webinar-phone-input"
                      className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors"
                      placeholder="+420 583 033 357"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                      Název školy *
                    </label>
                    <input
                      type="text"
                      name="school"
                      value={formData.school}
                      onChange={handleChange}
                      required
                      data-testid="webinar-school-input"
                      className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors"
                      placeholder="ZŠ a MŠ Praha"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={webinars.length === 0}
                    data-testid="webinar-submit-btn"
                    className="w-full bg-[color:var(--primary-500)] hover:bg-[color:var(--primary-600)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-xl shadow-md hover:shadow-lg transition-all"
                  >
                    Registrovat se na webinář
                  </button>

                  <p className="text-xs text-[color:var(--text-secondary)] text-center">
                    Odesláním formuláře souhlasíte se zpracováním osobních údajů
                  </p>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default WebinarRegistration;
